import {useMutation, UseMutationResult, useQueryClient} from '@tanstack/react-query';

import {Locale} from '../../../../provider/locale/locale';
import {useSnackbar} from '../../../../provider/snackbar/snackbar-hook';
import {postBulkReviewNotes} from '../../../../service/reviews/reviews';
import {getReviewNotesQueryKey} from '../../../../service/reviews/reviews-notes';
import {BulkReviewNotesResultType} from '../../../../service/reviews/reviews-type';

type BulkReviewNotesApplyMutationVariablesType = {
    reviewIds: Array<number>;
    text: string;
};

type UseBulkReviewNotesApplyMutationOptionsType = {
    refreshReviewsInfo: () => Promise<unknown>;
    onFlowComplete?: () => void;
};

export function useBulkReviewNotesApplyMutation(
    options: UseBulkReviewNotesApplyMutationOptionsType
): UseMutationResult<BulkReviewNotesResultType, unknown, BulkReviewNotesApplyMutationVariablesType> {
    const {refreshReviewsInfo, onFlowComplete} = options;
    const {snackbar} = useSnackbar();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({reviewIds, text}: BulkReviewNotesApplyMutationVariablesType) =>
            postBulkReviewNotes({reviewIds, text}),
        onError: () => {
            snackbar.error(<Locale stringKey="REVIEWS__BULK_NOTE__ERROR" />);
        },
        onSuccess: async (result, variables) => {
            await refreshReviewsInfo();
            await Promise.all(
                variables.reviewIds.map((reviewId) =>
                    queryClient.invalidateQueries({queryKey: getReviewNotesQueryKey(reviewId)})
                )
            );

            const processedCount = result.results.length;

            snackbar.success({
                message: <Locale stringKey="REVIEWS__BULK_NOTE__SUCCESS_TITLE" />,
                description: (
                    <Locale
                        stringKey="REVIEWS__BULK_NOTE__SUCCESS_DESCRIPTION"
                        valueMap={{processedCount: String(processedCount)}}
                    />
                ),
            });

            onFlowComplete?.();
        },
    });
}
